import React, { useState, useRef, useEffect } from 'react';
import { Button, Container, NavigationScroll } from '@uncinc/uncinc-react-kitchen-sink';
import { CoreLink } from '@uncinc/react-drupal-core';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import { useSelector } from 'react-redux';

import {
  MenuMainMenu,
  MenuMainMenuOverlay,
  MenuSecondaryMenu,
  MenuMainMenuMobile,
} from '../../implementations';

import {
  LanguageSwitcher,
} from '..';

import './style.scss';
import { LANGUAGE_ENGLISH } from '../../config/constants';

const Header = () => {
  const { t } = useTranslation();
  const searchRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [searchVisible, setSearchVisible] = useState(false);
  const [mobileMenuVisible, setMobileMenuVisible] = useState(false);
  const [inputValue, setInputValue] = useState(null);
  const history = useHistory();
  const currentLanguage = useSelector((state) => state.getIn(['ui', 'language']));

  useEffect(() => {
    if (searchRef.current && searchVisible) {
      searchRef.current.focus();
    }
  }, [searchRef, searchVisible]);

  // Prevent overlay from being displayed initially.
  window.addEventListener('load', (event) => {
    setLoaded(true);
  });

  useEffect(() => {
    if (document?.body && document.body.classList) {
      if (mobileMenuVisible) {
        document.body.classList.add('Header__overlay--body');
      }
      else {
        document.body.classList.remove('Header__overlay--body');
      }
    }
  }, [mobileMenuVisible]);

  const classes = c({
    expanded: mobileMenuVisible,
    preload: !loaded,
  });

  const header = useRef(null);

  const overlayIndex0 = useRef(null);
  const overlayIndex1 = useRef(null);
  const overlayIndex2 = useRef(null);
  const overlayIndex3 = useRef(null);

  const root = document.documentElement;

  const openOverlayDelay = React.useRef();

  const setActiveOverlayMenu = (
    index,
  ) => {
    // We disable the overlay menu for English, as there's not enough English content
    if (currentLanguage === LANGUAGE_ENGLISH) {
      return;
    }

    // We add a little time-out to the hover function to check for 'hover intent'.
    // We do however want to clear the previous animation if in the meanwhile
    // the user moved to a different menu item.
    clearTimeout(openOverlayDelay.current);
    if (index !== null) {
      openOverlayDelay.current = setTimeout(() => {
        header.current.className = `${classes} overlayActive overlayIndex__${index}`;
      }, 300);
    } else {
      header.current.className = classes;
    }
  };

  // This function sets the different max heights for the menu items, so we can animate the height nicely
  const setOverlayMaxHeights = () => {
    if (
      overlayIndex0.current &&
      overlayIndex1.current &&
      overlayIndex2.current &&
      overlayIndex3.current
    ) {
      root.style.setProperty('--menu-overlay-0-max-height', `${overlayIndex0.current.clientHeight}px`);
      root.style.setProperty('--menu-overlay-1-max-height', `${overlayIndex1.current.clientHeight}px`);
      root.style.setProperty('--menu-overlay-2-max-height', `${overlayIndex2.current.clientHeight}px`);
      root.style.setProperty('--menu-overlay-3-max-height', `${overlayIndex3.current.clientHeight}px`);
    } else {
      setTimeout(setOverlayMaxHeights, 50);
    }
  };

  useEffect(() => {
    setOverlayMaxHeights();
  }, []);

  const submitSearch = () => {
    if (mobileMenuVisible) setMobileMenuVisible(false);

    history.push({
      pathname: `/${currentLanguage}/zoeken`,
      search: `?query=${inputValue || ''}`
        + '&filters[0][identifier]=combined_language'
        + `&filters[0][value]=${currentLanguage}`
        + '&size=10',
    });
  };

  const isSearchInputEmpty = () => {
    if (inputValue && Boolean(inputValue.trim())) {
      return false;
    }

    return true;
  };

  const catchEnter = (e) => {
    if (e.key === 'Enter' && !isSearchInputEmpty()) {
      submitSearch();
    }
  };

  return (
    <NavigationScroll>
      <header ref={header} className={classes}>
        <div
          className="event-zone"
          onMouseLeave={() => setActiveOverlayMenu(null)}
        >
          <div className="wrapper">
            <Container fluid className="Header__container">
              <div className="Header">
                <div className={`Header__logo ${searchVisible ? 'mb-hidden' : ''}`}>
                  <CoreLink href={`/${currentLanguage}`} aria-label="To homepage" onClick={() => setActiveOverlayMenu(null)}>
                    <div className={`Header__logo__img ${currentLanguage === 'nl' ? 'nl-logo' : ''}`} />
                  </CoreLink>
                </div>
                <div className="Header__menu">
                  <div className="Header__menu__primary">
                    <MenuMainMenu
                      setActiveOverlayMenu={setActiveOverlayMenu}
                      overlayRefs={[overlayIndex0, overlayIndex1, overlayIndex2, overlayIndex3]}
                    />
                  </div>
                  <div className="Header__menu__secondary">
                    <div className={`Header__menu__secondary__search ${searchVisible ? 'visible' : ''}`}>
                      <input
                        onChange={(e) => { setInputValue(e.target.value); }}
                        onKeyDown={(e) => { catchEnter(e); }}
                        type="text"
                        name="search"
                        id="search"
                        placeholder={t('filters.search.placeholder')}
                        ref={searchRef}
                      />
                      <span
                        className="icon icon-cross"
                        onClick={() => { setSearchVisible(false); }}
                      />
                      {!isSearchInputEmpty() && <span
                        className="icon icon-search"
                        onClick={() => { submitSearch(); }}
                      />}
                    </div>

                    {/* Language switcher */}
                    <LanguageSwitcher setActiveOverlayMenu={setActiveOverlayMenu} />

                    {/* Secondary CTA menu */}
                    <MenuSecondaryMenu />
                  </div>
                </div>
                <div className="Header__buttons">
                  <Button
                    className="Header__buttons__search Button--header-icon"
                    onClick={() => { setSearchVisible(true); }}
                    aria-label="search"
                  >
                    <i className="icon icon-search" />
                  </Button>
                  <Button
                    className="Header__buttons__hamburger Button--header-icon"
                    onClick={() => { setMobileMenuVisible(!mobileMenuVisible); }}
                    aria-label="menu"
                  >
                    <i className={`icon icon-${mobileMenuVisible ? 'cross' : 'hamburger'}`} />
                  </Button>
                </div>
              </div>
            </Container>
          </div>
          <div className="Header__menu_overlay">
            <div className="wave-mask" />
            <div className="Header__menu_overlay__content">
              <Container fluid className="Header__container">
                <div className="row">
                  <div className="col-12">
                    <MenuMainMenuOverlay
                      setActiveOverlayMenu={setActiveOverlayMenu}
                      overlayRefs={[overlayIndex0, overlayIndex1, overlayIndex2, overlayIndex3]}
                    />
                  </div>
                </div>
              </Container>
            </div>
          </div>
        </div>
        <div className="Header__overlay">
          <Container>
            <div className="row">
              <div className="col-12">
                <MenuMainMenuMobile setMobileMenuVisible={setMobileMenuVisible} />
              </div>
            </div>
          </Container>
        </div>
        <div className="Header__backdrop" />
      </header>
    </NavigationScroll>
  );
};

export default Header;
